import { enqueueSnackbar } from 'notistack'
import React, { useContext, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router-dom'

import {
  TransferState,
  DefaultFetchDeviceFilters,
  DurationTestStatus,
} from '@/api/definition'
import DeviceSelectionTable from '@/components/DeviceSelectionTable'
import type {
  DeviceData,
  ValidationConfig,
} from '@/components/DeviceSelectionTable'
import { useAuthContext } from '@/hooks/useAuthContext'
import type { Device } from '@/pages/Device/Device'
import { SelectedDevicesContext } from '@/pages/inService/takeOutofService/reducers'
import { useDeviceTypeFilters } from '@/hooks/useTransalationForFilter'

interface SelectDevicesProps {
  setActiveStep: (step: number) => void
}
interface RowSelectableParams {
  row: Device
  id?: string | number
  [key: string]: unknown
}
interface TakeOutOfServiceLoaderData {
  initialDevices: Device[]
  rowCount: number
  loaderError?: string
}

const SelectDeviceForOutOfServiceStep: React.FC<SelectDevicesProps> = ({
  setActiveStep,
}) => {
  const { t } = useTranslation(['di', 'app', 'common'])
  const { profile } = useAuthContext()
  const { initialDevices, rowCount, loaderError } =
    useLoaderData<TakeOutOfServiceLoaderData>()

  const { state: selectedDevicesState, dispatch: selectedDevicesDispatch } =
    useContext(SelectedDevicesContext)
  const selectedDeviceIds = useMemo(
    () => Object.keys(selectedDevicesState.selectedDevices),
    [selectedDevicesState],
  )

  // Device validation function
  const validateDeviceEditable = useCallback(
    (device: Device): boolean => {
      if (device.editable) {
        return true
      }
      enqueueSnackbar(t('di:device.rights.producerOnlyError'), {
        variant: 'error',
      })
      return false
    },
    [t],
  )

  // Device filters for out of service (devices in service)
  const deviceFilters = useMemo(() => {
    return {
      ...DefaultFetchDeviceFilters,
      inService: true,
      durationTestStatus: profile?.productionOnly
        ? [DurationTestStatus.DurationTestCompleted]
        : [
            DurationTestStatus.DurationTestNotStarted,
            DurationTestStatus.DurationTestCompleted,
          ],
      transferStatus: [TransferState.DOMESTIC, TransferState.FOREIGN],
    }
  }, [profile?.productionOnly])

  // Column configuration - no project name for out of service
  const columnConfig = useMemo(
    () => ({
      includeProjectName: false,
    }),
    [],
  )

  // Validation configuration
  const validationConfig: ValidationConfig = useMemo(
    () => ({
      validateSelection: validateDeviceEditable,
      isRowSelectable: (params: RowSelectableParams) => params.row.editable,
      validationErrorMessage: t('di:device.deviceCannotBeEdited', {
        count: '{count}',
      }),
    }),
    [validateDeviceEditable, t],
  )

  // Handle selection changes
  const handleSelectionChange = useCallback(
    (newSelectedDevices: DeviceData[], deselectedIds: string[]) => {
      // Handle deselections first
      if (deselectedIds.length > 0) {
        selectedDevicesDispatch({
          type: 'DESELECT_MULTIPLE_DEVICES',
          uids: deselectedIds,
        })
      }

      // Handle new selections
      if (newSelectedDevices.length > 0) {
        selectedDevicesDispatch({
          type: 'SELECT_MULTIPLE_DEVICES',
          devices: newSelectedDevices,
        })
      }
    },
    [selectedDevicesDispatch],
  )

  // Handle next step
  const handleNext = useCallback(() => {
    setActiveStep(1)
  }, [setActiveStep])
  const filterConfig = useDeviceTypeFilters()
  return (
    <DeviceSelectionTable
      initialDevices={initialDevices}
      rowCount={rowCount}
      selectedDeviceIds={selectedDeviceIds}
      onSelectionChange={handleSelectionChange}
      onRowClick={validateDeviceEditable}
      onNext={handleNext}
      loaderError={loaderError}
      deviceFilters={deviceFilters}
      columnConfig={columnConfig}
      validationConfig={validationConfig}
      filterConfig={filterConfig}
      sharingConfig={{
        shareFilters: true,
        shareSearch: true,
        enableFilters: true,
        enableSearch: true,
      }}
      nextButtonProps={{
        text: t('common:general.next'),
        tooltip: t('app:device.intake.willPutAllIntoService'),
        color: 'success',
      }}
    />
  )
}

export default SelectDeviceForOutOfServiceStep
